/**
 * AIPlanExplainer — clickable "why this filter?" chip strip.
 *
 * Renders every filter the SociBaba AI derived from a shopper's natural
 * language query as a tappable chip. Clicking a chip expands a compact
 * reasoning panel below the strip explaining WHY the AI picked that
 * filter — sourced from `plan.filter_reasons[key]`.
 *
 * Falls back to a generic "This filter was inferred from your query
 * keywords." when the LLM didn't supply a per-filter reason.
 *
 * Used on:
 *   • /shop/search?semantic=1 (Sancta-parsed query panel)
 *   • Future — Intent Radar top-buyer reasoning · AI Cart match reasoning
 */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, HelpCircle, ChevronRight } from "lucide-react";

export interface SemanticPlan {
  keywords?: string | null;
  category?: string | null;
  brand?: string | null;
  min_price?: number | null;
  max_price?: number | null;
  min_score?: number | null;
  verified_only?: boolean | null;
  sort?: string | null;
  explanation?: string | null;
  filter_reasons?: Record<string, string | null> | null;
}

type ChipKey = "keywords" | "category" | "brand" | "min_price" | "max_price"
             | "min_score" | "verified_only" | "sort";

interface ChipDef {
  key: ChipKey;
  label: string;
  emerald?: boolean;  // when true, chip uses emerald tint (verified)
}

function _formatChips(plan: SemanticPlan): ChipDef[] {
  const chips: ChipDef[] = [];
  if (plan.category) chips.push({ key: "category", label: String(plan.category) });
  if (plan.brand) chips.push({ key: "brand", label: `Brand · ${plan.brand}` });
  if (plan.min_score != null) chips.push({ key: "min_score", label: `SibaScore ≥ ${plan.min_score}` });
  if (plan.min_price != null) chips.push({ key: "min_price", label: `Over ₹${Number(plan.min_price).toLocaleString("en-IN")}` });
  if (plan.max_price != null) chips.push({ key: "max_price", label: `Under ₹${Number(plan.max_price).toLocaleString("en-IN")}` });
  if (plan.verified_only) chips.push({ key: "verified_only", label: "Verified brands only", emerald: true });
  if (plan.sort && plan.sort !== "score") {
    chips.push({ key: "sort", label: `Sort · ${plan.sort.replace(/_/g, " ")}` });
  }
  if (plan.keywords && plan.keywords.trim()) {
    chips.unshift({ key: "keywords", label: `Words · ${plan.keywords}` });
  }
  return chips;
}

const _fallback = "This filter was inferred from your query keywords.";

interface Props {
  plan: SemanticPlan | null | undefined;
  className?: string;
}

export const AIPlanExplainer: React.FC<Props> = ({ plan, className = "" }) => {
  const [expanded, setExpanded] = useState<ChipKey | null>(null);
  if (!plan) return null;

  const chips = _formatChips(plan);
  if (chips.length === 0 && !plan.explanation) return null;

  const expandedReason = expanded
    ? (plan.filter_reasons?.[expanded] || _fallback)
    : null;

  return (
    <div
      data-testid="shop-search-ai-plan"
      className={`rounded-2xl border border-fuchsia-200 bg-fuchsia-50 dark:bg-fuchsia-950/30 dark:border-fuchsia-800 px-4 py-3 ${className}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-fuchsia-700 dark:text-fuchsia-300 font-bold">
        <Sparkles className="h-3 w-3" /> Sancta parsed your query
        <span className="ml-auto text-fuchsia-500 dark:text-fuchsia-400 normal-case tracking-normal text-[10px] font-medium">
          Tap any chip for the reasoning
        </span>
      </div>
      {plan.explanation && (
        <div className="text-sm text-fuchsia-900 dark:text-fuchsia-100 mt-1">
          {plan.explanation}
        </div>
      )}

      {/* Chip strip */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {chips.map((c) => {
          const active = expanded === c.key;
          const emerald = c.emerald;
          const base = emerald
            ? "bg-emerald-100 dark:bg-emerald-900/40 border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-200/70"
            : "bg-white/60 dark:bg-fuchsia-900/40 border-fuchsia-300 dark:border-fuchsia-700 text-fuchsia-800 dark:text-fuchsia-200 hover:bg-white";
          return (
            <button
              key={c.key}
              type="button"
              data-testid={`shop-search-ai-chip-${c.key}`}
              onClick={() => setExpanded(active ? null : c.key)}
              aria-pressed={active}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold transition-colors ${base} ${active ? "ring-2 ring-fuchsia-400/50" : ""}`}
            >
              <span>{c.label}</span>
              <HelpCircle className="h-2.5 w-2.5 opacity-60" strokeWidth={2.5} />
            </button>
          );
        })}
      </div>

      {/* Reasoning panel — expands beneath the chips */}
      <AnimatePresence initial={false}>
        {expanded && expandedReason && (
          <motion.div
            key={`reason-${expanded}`}
            data-testid={`shop-search-ai-reason-${expanded}`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="mt-3 rounded-xl border border-fuchsia-300 bg-white dark:bg-fuchsia-950/60 dark:border-fuchsia-700 px-3 py-2.5 flex items-start gap-2">
              <div className="mt-[3px] shrink-0">
                <ChevronRight className="h-3.5 w-3.5 text-fuchsia-500" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-300 font-bold mb-0.5">
                  Why this filter?
                </div>
                <div className="text-sm text-fuchsia-900 dark:text-fuchsia-100 leading-snug">
                  {expandedReason}
                </div>
              </div>
              <button
                onClick={() => setExpanded(null)}
                className="ml-auto shrink-0 text-fuchsia-400 hover:text-fuchsia-700 text-[10px] font-bold uppercase tracking-widest"
                aria-label="Close reasoning"
              >
                Close
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIPlanExplainer;
